import { productHasGiftTag, normalizeGiftTagSlug } from './productGiftTags';
import { normalizeAutoProductTags } from './content/storefrontMerchandisingMerge';

function productKey(product) {
  if (!product?.category || product.id == null) return '';
  return `${product.category}:${String(product.id)}`;
}

function parseCreatedAtMs(raw) {
  const s = String(raw || '').trim();
  if (!s) return NaN;
  const ms = Date.parse(s.includes('T') ? s : s.replace(' ', 'T'));
  return Number.isFinite(ms) ? ms : NaN;
}

/**
 * Whether a catalog product counts as a new arrival (New badge / created_at window).
 * @param {object} product
 * @param {object} [autoTagCfg] — store_copy.auto_product_tags
 * @param {Date} [now]
 */
export function isNewArrivalProduct(product, autoTagCfg, now = new Date()) {
  if (productHasGiftTag(product, 'new')) return true;
  const auto = Array.isArray(product?.auto_tags) ? product.auto_tags : [];
  if (auto.some((t) => normalizeGiftTagSlug(t) === 'new')) return true;

  const cfg = normalizeAutoProductTags(autoTagCfg);
  if (!cfg.enabled || !cfg.new_enabled) return false;

  const created = parseCreatedAtMs(product?.created_at);
  if (!Number.isFinite(created)) return false;

  const since = Date.parse(`${cfg.new_since}T00:00:00`);
  const cutoff = now.getTime() - cfg.new_days * 24 * 60 * 60 * 1000;
  if (Number.isFinite(since) && created < since) return false;
  return created >= cutoff;
}

function sortNewestFirst(products) {
  return [...products].sort((a, b) => {
    const da = parseCreatedAtMs(a.created_at);
    const db = parseCreatedAtMs(b.created_at);
    const aOk = Number.isFinite(da);
    const bOk = Number.isFinite(db);
    if (aOk && bOk && db !== da) return db - da;
    if (aOk !== bOk) return aOk ? -1 : 1;
    return String(b.id || '').localeCompare(String(a.id || ''), undefined, { numeric: true });
  });
}

/**
 * Newest products for the home New arrivals shelf.
 * Prefers New-tagged / in-window items; falls back to newest catalog pieces so the shelf stays visible.
 * @param {Array<object>} allProducts
 * @param {{ excludeProducts?: Array, limit?: number, autoTagCfg?: object, now?: Date }} [options]
 */
export function resolveHomeNewArrivals(
  allProducts,
  { excludeProducts = [], limit = 12, autoTagCfg, now = new Date() } = {},
) {
  const list = Array.isArray(allProducts) ? allProducts : [];
  const max = Math.max(1, limit);
  const exclude = new Set(
    (Array.isArray(excludeProducts) ? excludeProducts : []).map(productKey).filter(Boolean),
  );

  const withImage = (items, useExclude) =>
    items.filter((p) => {
      if (!p?.image) return false;
      const key = productKey(p);
      if (!key) return false;
      if (useExclude && exclude.has(key)) return false;
      return true;
    });

  const tagged = withImage(list, true).filter((p) => isNewArrivalProduct(p, autoTagCfg, now));
  if (tagged.length > 0) {
    return sortNewestFirst(tagged).slice(0, max);
  }

  // No New badges yet — show newest catalog items (prefer excluding Featured, then fill).
  const newestExcluding = sortNewestFirst(withImage(list, true));
  if (newestExcluding.length >= Math.min(4, max) || exclude.size === 0) {
    return newestExcluding.slice(0, max);
  }

  return sortNewestFirst(withImage(list, false)).slice(0, max);
}
